/* eslint-disable react-hooks/exhaustive-deps */
import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react"
import {useReactToPrint} from "react-to-print"
import {Branding} from "types/branding"
import {useModel} from "hooks"
import {clientsService} from "services"
import CheckInCheckOutPdfPage from "sections/Admin/DailyAttendanceCheckInCheckOutReport/CheckInCheckOutPdfPage"
import {CheckInCheckOutStudent} from "sections/Admin/DailyAttendanceCheckInCheckOutReport/checkInCheckOutReport"
import styles from "./DailyAttendanceCheckInCheckOutPdf.module.css"

export type DailyAttendancePdfPayload = {
    students: CheckInCheckOutStudent[]
    dateLabel: string
    campuses?: string[]
    programs?: string[]
    phases?: string[]
    shifts?: string[]
}

export type DailyAttendanceCheckInCheckOutPdfHandle = {
    print: (payload: DailyAttendancePdfPayload) => void
}

type Props = {
    onFinished?: () => void
}

const convertImgToBase64 = (url: string, callback: (base64Img: string) => void) => {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    const img = new Image()
    img.crossOrigin = "Anonymous"
    img.onload = function () {
        canvas.height = img.height
        canvas.width = img.width
        ctx?.drawImage(img, 0, 0)
        callback(canvas.toDataURL("image/png"))
    }

    img.src = url
}

const DailyAttendanceCheckInCheckOutPdf = forwardRef<DailyAttendanceCheckInCheckOutPdfHandle, Props>(
    ({onFinished}, ref) => {
        const model = useModel()
        const pdfRef = useRef<HTMLDivElement>(null)
        const promiseResolveRef = useRef<(() => void) | null>(null)
        const [downloading, setDownloading] = useState(false)
        const [logoBase64, setLogoBase64] = useState("")
        const [clientData, setClientData] = useState<any>()
        const requestIdRef = useRef(0)
        const [request, setRequest] = useState<{id: number; payload: DailyAttendancePdfPayload} | null>(null)

        const logo =
            clientData?.college_logo || model.clientSetting?.branding?.[Branding.BrandingType.OfficialLogoSquare]
        const collegeName = model.clientSetting?.name
        const timeFormat = model.getUserTimeFormat()
        const dateFormat = model.getUserDateFormat()

        useImperativeHandle(ref, () => ({
            print: (payload) => {
                requestIdRef.current += 1
                setRequest({id: requestIdRef.current, payload})
            }
        }))

        useEffect(() => {
            clientsService
                .getClientDetail()
                .then(setClientData)
                .catch(() => undefined)
        }, [])

        useEffect(() => {
            if (!logo) return
            convertImgToBase64(`${logo}?t=${Date.now()}`, setLogoBase64)
        }, [logo])

        useEffect(() => {
            if (downloading && promiseResolveRef.current) {
                promiseResolveRef.current()
            }
        }, [downloading])

        const handlePrint = useReactToPrint({
            contentRef: pdfRef,
            documentTitle: "Daily_Attendance_Check_In_Check_Out_Report",
            pageStyle: `
            @page {
                size: portrait;
                margin: 10px;
            }
            @media print {
                html, body {
                    height: initial !important;
                    overflow: initial !important;
                    -webkit-print-color-adjust: exact;
                }
                table {
                    width: 100% !important;
                }
                th, td {
                    font-size: 8px !important;
                    padding: 2px !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                    white-space: normal !important;
                }
            }
        `,
            onBeforePrint: () => {
                return new Promise((resolve) => {
                    setDownloading(true)
                    promiseResolveRef.current = resolve
                })
            },
            onAfterPrint: () => {
                promiseResolveRef.current = null
                setDownloading(false)
                onFinished?.()
            },
            onPrintError: () => {
                promiseResolveRef.current = null
                setDownloading(false)
                onFinished?.()
            }
        })

        const handlePrintRef = useRef(handlePrint)
        handlePrintRef.current = handlePrint

        useEffect(() => {
            if (!request) return
            handlePrintRef.current?.()
        }, [request])

        if (!request) return null

        const {payload} = request

        return (
            <div className={styles.host} aria-hidden>
                <CheckInCheckOutPdfPage
                    pdfRef={pdfRef}
                    downloading={downloading}
                    logoBase64={logoBase64}
                    collegeName={collegeName}
                    dateLabel={payload.dateLabel}
                    campuses={payload.campuses}
                    programs={payload.programs}
                    phases={payload.phases}
                    shifts={payload.shifts}
                    students={payload.students}
                    timeFormat={timeFormat}
                    dateFormat={dateFormat}
                />
            </div>
        )
    }
)

export default DailyAttendanceCheckInCheckOutPdf
